import { Fragment } from "react";

import { css } from "@compiled/react";

import { media } from "@atlaskit/primitives/responsive";

import {
    AtlSiteType,
    useLogMeIn
} from "@/components/confluence/hooks/variant-cfind-1849/use-log-me-in";
import { TypographyStyles } from "@/libraries/wpl/foundations/src";
import { GlobalFooter } from "@/patterns/global-footer/src";
import { ConfluencePageBanner } from "@/templates/confluence-software-page/src/sections/banner";
import { withFeatureExposed } from "@/utils/feature-exposed";
import { ParentScrollTracking } from "@/utils/scroll-tracking";

import { ConfluenceSoftwarePageProps } from "../../../pages/confluence/confluence-software-page";

import AiSection from "./sections/ai-section";
import ConfluenceSoftwareNav from "./sections/confluence-navbar";
import ContentTypesSection from "./sections/content-types-section";
import CtaSection from "./sections/cta-section";
import FeelSupportedSection from "./sections/feel-supported-section";
import HeroTextSection from "./sections/hero-text-section";
import IntegrationsSection from "./sections/integrations-section";
import ScrollerSection from "./sections/scroller-section";
import SocialEndorsementSection from "./sections/social-endorsement-section";
import { TeamTourFranceBanner } from "./sections/team-tour-france-banner";
import TemplatesSection from "./sections/templates-section";
import TestimonialsSection from "./sections/testimonial-section";
import HeroSection from "./sections/variant-cfind-1849/hero";
import WhyConfluenceSection from "./sections/why-confluence-section";

const relativeParent = css({
    position: "relative",
    display: "flex",
    justifyContent: "center",
    overflow: "hidden"
});

const relativeChildren = css({
    display: "flex",
    flexDirection: "column",
    position: "relative",
    marginBlockStart: "6.5rem",
    gap: "6rem",

    [media.below.md]: {
        gap: "13rem",
        marginBlockStart: "2rem"
    }
});

const backgroundShape = css({
    clipPath: "polygon(0 7%, 99.25% 0, 100% 100%, 0 100%)",
    backgroundColor: "#F8F8F8",
    position: "absolute",
    insetBlockStart: 0,
    overflow: "hidden",
    width: 1464,
    height: 1060,

    [media.below.md]: {
        clipPath: "none",
        height: 700
    }
});

const ConfluenceSoftwarePageComponent = ({
    availableProducts,
    userProfile
}: ConfluenceSoftwarePageProps) => {
    const { userProfileName, recentSites, shouldShowLogMeIn } = useLogMeIn({
        availableProducts,
        userProfile,
        siteType: AtlSiteType.CONFLUENCE
    });

    return (
        <Fragment>
            <ConfluenceSoftwareNav />
            <TypographyStyles>
                <ParentScrollTracking
                    scrollTrackingEnabled={true}
                    pageId="confluence-software-page-cfind-1234"
                >
                    <TeamTourFranceBanner dataProductKey="confluence" />
                    <ConfluencePageBanner />
                    {shouldShowLogMeIn ? (
                        <HeroSection
                            confluenceSites={recentSites}
                            userProfileName={userProfileName}
                        />
                    ) : (
                        <HeroTextSection />
                    )}
                    <ContentTypesSection />
                    <SocialEndorsementSection />
                    <ScrollerSection />
                    <TemplatesSection />
                    <WhyConfluenceSection />
                    <TestimonialsSection />
                    <div css={relativeParent}>
                        <div css={backgroundShape} />
                        <div css={relativeChildren}>
                            <IntegrationsSection />
                            <AiSection />
                        </div>
                    </div>
                    <FeelSupportedSection />
                    <CtaSection />
                </ParentScrollTracking>
            </TypographyStyles>
            <GlobalFooter />
        </Fragment>
    );
};

export const ConfluenceSoftwarePage = withFeatureExposed(
    "cfind-1234-product-tour-refresh-q3fy24",
    "Test",
    ConfluenceSoftwarePageComponent
);
